import type { FormEvent, JSX } from 'react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useInviteGuardian } from '@/features/guardian/hooks';
import {
  ACCESS_SCOPE_DESCRIPTIONS,
  ACCESS_SCOPE_LABELS,
  RELATIONSHIP_LABELS,
} from '@/features/guardian/labels';
import type {
  GuardianAccessScope,
  GuardianRelationshipType,
  InviteGuardianResult,
} from '@/features/guardian/types';
import { ApiError } from '@/lib/api/client';

const RELATIONSHIP_OPTIONS: readonly GuardianRelationshipType[] = [
  'FATHER',
  'MOTHER',
  'GUARDIAN',
  'CAREGIVER',
  'OTHER',
];
const ACCESS_SCOPE_OPTIONS: readonly GuardianAccessScope[] = ['FULL_READ', 'LIMITED_READ'];

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function InviteGuardianDialog({
  studentId,
  open,
  onOpenChange,
  onInvited,
}: {
  readonly studentId: string;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly onInvited: (result: InviteGuardianResult) => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [relationshipType, setRelationshipType] = useState<GuardianRelationshipType | ''>('');
  const [accessScope, setAccessScope] = useState<GuardianAccessScope | ''>('');
  const [validationError, setValidationError] = useState<string | null>(null);
  const invite = useInviteGuardian(studentId);

  function reset(): void {
    setEmail('');
    setRelationshipType('');
    setAccessScope('');
    setValidationError(null);
    invite.reset();
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      reset();
    }
    onOpenChange(next);
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setValidationError(null);

    if (!EMAIL_PATTERN.test(email.trim())) {
      setValidationError('Masukkan alamat email yang valid.');
      return;
    }
    if (relationshipType === '') {
      setValidationError('Pilih hubungan dengan santri.');
      return;
    }
    if (accessScope === '') {
      setValidationError('Pilih akses yang diberikan.');
      return;
    }

    invite.mutate(
      { email: email.trim(), relationshipType, accessScope },
      {
        onSuccess: (result) => {
          onInvited(result);
          handleOpenChange(false);
        },
      },
    );
  }

  const apiErrorMessage =
    invite.error instanceof ApiError
      ? invite.error.message
      : invite.error !== null
        ? 'Terjadi kesalahan. Silakan coba lagi.'
        : null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Undang Wali</DialogTitle>
          <DialogDescription>
            Undangan akan mengirimkan tautan penerimaan yang dapat Anda salin dan kirim secara
            manual.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="guardian-email">Email</Label>
            <Input
              id="guardian-email"
              type="text"
              inputMode="email"
              value={email}
              onChange={(event) => {
                setEmail(event.target.value);
              }}
              placeholder="wali@contoh.com"
              autoComplete="email"
            />
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="guardian-relationship">Hubungan</Label>
            <Select
              value={relationshipType}
              onValueChange={(value) => {
                setRelationshipType(value as GuardianRelationshipType);
              }}
            >
              <SelectTrigger id="guardian-relationship">
                <SelectValue placeholder="Pilih hubungan" />
              </SelectTrigger>
              <SelectContent>
                {RELATIONSHIP_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {RELATIONSHIP_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="flex flex-col gap-2">
            <Label htmlFor="guardian-access-scope">Akses</Label>
            <Select
              value={accessScope}
              onValueChange={(value) => {
                setAccessScope(value as GuardianAccessScope);
              }}
            >
              <SelectTrigger id="guardian-access-scope">
                <SelectValue placeholder="Pilih akses" />
              </SelectTrigger>
              <SelectContent>
                {ACCESS_SCOPE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {ACCESS_SCOPE_LABELS[option]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {accessScope !== '' && (
              <p className="text-xs text-muted-foreground">
                {ACCESS_SCOPE_DESCRIPTIONS[accessScope]}
              </p>
            )}
          </div>

          {validationError !== null && (
            <Alert variant="destructive">
              <AlertDescription>{validationError}</AlertDescription>
            </Alert>
          )}
          {apiErrorMessage !== null && (
            <Alert variant="destructive">
              <AlertDescription>{apiErrorMessage}</AlertDescription>
            </Alert>
          )}

          <DialogFooter>
            <Button type="submit" disabled={invite.isPending}>
              {invite.isPending ? 'Mengirim...' : 'Kirim Undangan'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
